import type { MoneyRange } from '@wolhasudang/shared';
import { calculateShiftMinutes } from './time.js';
import type {
  AllowanceComparison, BasisNode, CalcInput, EvidenceGrade, MonthResult, PaycheckInput, PayItem, PrivateProfile,
  PublicServantProfile, RateTables, ReconciliationRow, ReconciliationSummary, Shift, ShiftType, ActualDepositReconciliation, Workplace, WorkplaceBreakdown,
} from './types.js';
import { calculateTaxDeduction } from './tax.js';
import { calculateWeeklyHolidayPeriod } from './weekly-holiday.js';
import { termFor } from './workplaces.js';

function range(min: number, max = min): MoneyRange {
  return { min, max };
}

function roundedMoney(minutes: number, hourly: number, numerator: number, denominator: number): number {
  return Math.round((minutes * hourly * numerator) / (60 * denominator));
}

function addRanges(values: MoneyRange[]): MoneyRange {
  return values.reduce((total, current) => ({ min: total.min + current.min, max: total.max + current.max }), range(0));
}

function lowerGrade(current: EvidenceGrade, next: EvidenceGrade): EvidenceGrade {
  const order: EvidenceGrade[] = ['A', 'B', 'C'];
  return order.indexOf(next) > order.indexOf(current) ? next : current;
}

function shiftClock(shift: Shift, shiftType: ShiftType): { start: string; end: string; confirmed: boolean } {
  if (shift.actualConfirmed && shift.actualStart && shift.actualEnd) {
    return { start: shift.actualStart, end: shift.actualEnd, confirmed: true };
  }
  return {
    start: shift.scheduledStart ?? shiftType.start,
    end: shift.scheduledEnd ?? shiftType.end,
    confirmed: false,
  };
}

function privateMonth(input: CalcInput, profile: PrivateProfile): MonthResult {
  const missingInputs: string[] = [];
  const warnings: string[] = [];
  let grade: EvidenceGrade = 'A';
  const firstRecordedDate = input.shifts.filter((shift) => shift.date >= input.period.start && shift.date <= input.period.end).map((shift) => shift.date).sort()[0] ?? input.period.start;
  const resolvedTerm = input.wageTerms && profile.workplaceId ? termFor(input.wageTerms, profile.workplaceId, firstRecordedDate) : undefined;
  const hourly = resolvedTerm?.ordinaryHourlyRate ?? profile.ordinaryHourlyRate;
  if (!hourly || (!profile.ordinaryWageConfirmed && !resolvedTerm?.confirmedAt)) {
    missingInputs.push('ordinaryHourlyRateConfirmed');
    grade = 'C';
  }
  if (!profile.establishmentConfirmed || profile.firmSize === 'unknown') {
    missingInputs.push('establishmentSizeConfirmed');
    grade = 'C';
  }
  if (hourly && input.tables.minimumWage && hourly < input.tables.minimumWage.hourly) warnings.push('BELOW_MINIMUM_WAGE');
  const supportedScheme = profile.workTimeScheme === 'standard';
  if (!supportedScheme) {
    missingInputs.push('supportedWorkTimeScheme');
    grade = 'C';
    warnings.push('UNSUPPORTED_WORK_TIME_SCHEME');
  }

  let overtimeMinutes = 0;
  let holidayMinutes = 0;
  let holidayOverMinutes = 0;
  let nightMin = 0;
  let nightMax = 0;

  for (const shift of input.shifts) {
    if (shift.date < input.period.start || shift.date > input.period.end) continue;
    const shiftType = input.shiftTypes[shift.shiftTypeId];
    if (!shiftType || shiftType.kind !== 'work') continue;
    if (shift.actualBreakUnconfirmed) {
      grade = 'C';
      warnings.push('ACTUAL_BREAK_UNCONFIRMED');
      continue;
    }
    const clock = shiftClock(shift, shiftType);
    if (!clock.confirmed) grade = lowerGrade(grade, 'B');
    const breaks = shift.actualConfirmed ? shift.actualBreakRanges : shiftType.breakRanges;
    const unknownBreakMinutes = breaks?.length ? undefined : shift.actualConfirmed ? shift.actualBreakMinutes ?? shiftType.breakMinutes : shiftType.breakMinutes;
    const minutes = calculateShiftMinutes(clock.start, clock.end, breaks, unknownBreakMinutes);
    if (minutes.night.min !== minutes.night.max) grade = lowerGrade(grade, 'B');
    nightMin += minutes.night.min;
    nightMax += minutes.night.max;
    if (shift.holidayOverride === 'holiday') {
      holidayMinutes += Math.min(480, minutes.worked);
      holidayOverMinutes += Math.max(0, minutes.worked - 480);
    } else if (supportedScheme) {
      overtimeMinutes += Math.max(0, minutes.worked - 480);
    }
  }

  if ((overtimeMinutes > 0 || holidayMinutes > 0 || holidayOverMinutes > 0)
    && input.tables.privateRulePolicy.classificationReview.dailyOvertimeAndHoliday !== 'reviewed') {
    grade = 'C';
    warnings.push('LEGAL_REVIEW_REQUIRED_OVERTIME_HOLIDAY');
  }

  const items: PayItem[] = [];
  const basis: BasisNode[] = [];
  const policy = input.tables.privateRulePolicy;
  const premiumApplies = profile.firmSize === 'fivePlus' && Boolean(hourly);
  if (!premiumApplies && profile.firmSize === 'under5') warnings.push('STATUTORY_PREMIUM_NOT_APPLIED_UNDER5');

  const addItem = (
    kind: PayItem['kind'],
    minutes: MoneyRange,
    rate: { numerator: number; denominator: number },
  ) => {
    if (!hourly || !premiumApplies || (minutes.min === 0 && minutes.max === 0)) return;
    const amount = range(
      roundedMoney(minutes.min, hourly, rate.numerator, rate.denominator),
      roundedMoney(minutes.max, hourly, rate.numerator, rate.denominator),
    );
    const basisId = `basis-${kind}`;
    items.push({ kind, amount, minutes, basisId });
    basis.push({
      id: basisId,
      formula: 'minutes × ordinaryHourlyRate ÷ 60 × premiumRate',
      variables: [
        { name: 'minutes', value: `${minutes.min}-${minutes.max}`, source: 'derived' },
        { name: 'ordinaryHourlyRate', value: String(hourly), source: 'user' },
        { name: 'premiumRate', value: `${rate.numerator}/${rate.denominator}`, source: 'table' },
      ],
      source: policy.source,
      assumptions: ['근로시간제·사업장 규모·휴일 분류는 사용자 확인값을 사용한다.'],
    });
  };

  if (supportedScheme) addItem('overtimePremium', range(overtimeMinutes), policy.overtimePremium);
  addItem('nightPremium', range(nightMin, nightMax), policy.nightPremium);
  addItem('holidayPremium', range(holidayMinutes), policy.holidayPremium);
  addItem('holidayOverPremium', range(holidayOverMinutes), policy.holidayOverPremium);

  let weeklyHoliday: MonthResult['weeklyHoliday'];
  if (input.weeklyHolidayWeeks) {
    const weekly = calculateWeeklyHolidayPeriod(input.weeklyHolidayWeeks.filter((week) => week.holidayDate >= input.period.start && week.holidayDate <= input.period.end));
    weeklyHoliday = weekly.weeks;
    if (weekly.amount > 0 && hourly) {
      const weeklyItem: PayItem = { kind: 'weeklyHoliday', amount: range(weekly.amount), basisId: 'basis-weeklyHoliday' };
      items.push(weeklyItem);
      basis.push({
        id: 'basis-weeklyHoliday', formula: '주 소정근로시간 ÷ max(소정근로일, 5) × 통상시급',
        variables: [
          { name: 'weeklyScheduledMinutes', value: String(weekly.weeks.reduce((sum, week) => sum + week.paidMinutes, 0)), source: 'user' },
          { name: 'weeklyHolidayWeekCount', value: String(weekly.weeks.length), source: 'derived' },
          { name: 'ordinaryHourlyRate', value: String(hourly), source: 'user' },
        ], source: policy.source, assumptions: ['주별 중간 반올림 없이 정산기간 합계에서 한 번 반올림한다.'],
      });
    }
    grade = lowerGrade(grade, weekly.grade);
    if (weekly.weeks.some((week) => week.grade === 'B')) warnings.push('WEEKLY_HOLIDAY_RANGE');
  } else if (profile.segment === 'hourly') {
    missingInputs.push('weeklyScheduledHoursConfirmed');
    grade = 'C';
  }
  if (input.wageTerms?.length && profile.workplaceId) {
    const missingTerm = input.shifts.some((shift) => shift.date >= input.period.start && shift.date <= input.period.end && !termFor(input.wageTerms!, profile.workplaceId!, shift.date));
    if (missingTerm) { missingInputs.push('MISSING_WAGE_TERM'); grade = 'C'; }
  }

  for (const allowance of input.allowances) {
    if (!Number.isInteger(allowance.amount) || allowance.amount < 0) throw new Error('INVALID_ALLOWANCE_AMOUNT');
    const matchingShifts = input.shifts.filter((shift) => shift.date >= input.period.start && shift.date <= input.period.end);
    const quantity = allowance.mode === 'monthly' ? 1
      : allowance.mode === 'perNight'
        ? matchingShifts.filter((shift) => input.shiftTypes[shift.shiftTypeId]?.countsAsNight).length
        : matchingShifts.filter((shift) => shift.shiftTypeId === allowance.shiftTypeId).length;
    if (allowance.mode === 'perShiftType' && !allowance.shiftTypeId) throw new Error('MISSING_ALLOWANCE_SHIFT_TYPE');
    const resolvedAmount = allowance.amount * quantity;
    const basisId = `basis-custom-${allowance.id}`;
    items.push({ kind: 'customAllowance', amount: range(resolvedAmount), basisId, customAllowanceId: allowance.id });
    basis.push({
      id: basisId,
      formula: allowance.mode === 'monthly' ? 'userConfiguredMonthlyAmount' : 'unitAmount × matchingShiftCount',
      variables: [
        { name: 'unitAmount', value: String(allowance.amount), source: 'user' },
        { name: 'matchingShiftCount', value: String(quantity), source: 'derived' },
      ],
      source: policy.source,
      assumptions: allowance.coverageConfirmed ? [] : ['법정수당 포함 범위가 확인되지 않아 차액 비교에서 제외한다.'],
    });
    if (!allowance.coverageConfirmed) grade = lowerGrade(grade, 'C');
  }

  const taxPreset = input.taxPreset ?? resolvedTerm?.taxPreset ?? input.wageTerms?.find((term) => term.workplaceId === profile.workplaceId)?.taxPreset;
  const deductionReference = taxPreset ? calculateTaxDeduction(taxPreset, input.insuranceBaseMonthly, input.tables) : undefined;
  return {
    items,
    allowanceSubtotal: addRanges(items.map((item) => item.amount)),
    evidenceGrade: grade,
    missingInputs,
    warnings,
    basis,
    provenance: {
      calculationSchemaVersion: input.calculationSchemaVersion,
      tableVersions: input.tables.versions,
      calculatedAt: input.calculatedAt ?? new Date().toISOString(),
    },
    ...(weeklyHoliday ? { weeklyHoliday } : {}),
    ...(deductionReference ? { deductionReference } : {}),
  };
}

function aggregatePrivateResults(entries: Array<{ workplace: Workplace; result: MonthResult }>, input: CalcInput): MonthResult {
  const allowanceModes = new Map(input.allowances.map((allowance) => [allowance.id, allowance.mode]));
  const itemGroups = new Map<string, { item: PayItem; basis?: BasisNode; included: boolean }>();
  const monthlySeen = new Set<string>();
  const missingInputs = new Set<string>();
  const warnings = new Set<string>();
  const weeklyHoliday = entries.flatMap(({ result }) => result.weeklyHoliday ?? []);
  let grade: EvidenceGrade = 'A';
  for (const { result } of entries) {
    grade = lowerGrade(grade, result.evidenceGrade);
    result.missingInputs.forEach((value) => missingInputs.add(value));
    result.warnings.forEach((value) => warnings.add(value));
    for (const item of result.items) {
      const key = item.customAllowanceId ? `custom:${item.customAllowanceId}` : `kind:${item.kind}`;
      const monthly = item.customAllowanceId !== undefined && allowanceModes.get(item.customAllowanceId) === 'monthly';
      if (monthly && monthlySeen.has(key)) continue;
      const basis = result.basis.find((candidate) => candidate.id === item.basisId);
      const existing = itemGroups.get(key);
      if (!existing) {
        itemGroups.set(key, { item: { ...item, basisId: `basis-workplace-${key.replace(/[^a-zA-Z0-9_-]/g, '-')}` }, ...(basis ? { basis } : {}), included: true });
      } else if (!monthly) {
        existing.item = { ...existing.item, amount: addRanges([existing.item.amount, item.amount]) };
        if (existing.basis && basis) existing.basis = { ...existing.basis, assumptions: [...new Set([...existing.basis.assumptions, ...basis.assumptions, '사업장별 기준을 합산한 결과예요. 사업장별 항목에서 각 기준을 확인하세요.'])] };
      }
      if (monthly) monthlySeen.add(key);
    }
  }
  const items = [...itemGroups.values()].map(({ item }) => item);
  const basis = [...itemGroups.values()].flatMap(({ item, basis: source }) => source ? [{ ...source, id: item.basisId }] : []);
  const workplaceBreakdown: WorkplaceBreakdown[] = entries.map(({ workplace, result }) => ({
    workplaceId: workplace.id, name: workplace.name, allowanceSubtotal: result.allowanceSubtotal, evidenceGrade: result.evidenceGrade, items: result.items,
  }));
  const provenance = entries[0]?.result.provenance ?? {
    calculationSchemaVersion: input.calculationSchemaVersion, tableVersions: input.tables.versions, calculatedAt: input.calculatedAt ?? new Date().toISOString(),
  };
  return {
    items, allowanceSubtotal: addRanges(items.map((item) => item.amount)), evidenceGrade: grade,
    missingInputs: [...missingInputs], warnings: [...warnings], basis, provenance,
    ...(weeklyHoliday.length ? { weeklyHoliday } : {}),
    ...(entries.length > 1 ? { workplaceBreakdown } : {}),
  };
}

function multiWorkplacePrivateMonth(input: CalcInput, profile: PrivateProfile, workplaces: Workplace[]): MonthResult {
  const periodShifts = input.shifts.filter((shift) => shift.date >= input.period.start && shift.date <= input.period.end);
  const defaultId = profile.workplaceId ?? workplaces[0]?.id;
  const workplaceIds = [...new Set(periodShifts.map((shift) => shift.workplaceId ?? defaultId).filter((id): id is string => Boolean(id)))];
  if (workplaceIds.length <= 1) return privateMonth(input, profile);
  const entries = workplaceIds.map((workplaceId) => {
    const workplace = workplaces.find((candidate) => candidate.id === workplaceId) ?? {
      id: workplaceId, name: '확인되지 않은 직장', colorToken: 'blue', scale: profile.firmSize,
      scaleConfirmedAt: profile.establishmentConfirmed ? new Date().toISOString() : null, segment: profile.segment ?? 'shift', archivedAt: null, createdAt: new Date().toISOString(),
    } satisfies Workplace;
    const scopedProfile: PrivateProfile = {
      ...profile, workplaceId, firmSize: workplace.scale,
      establishmentConfirmed: profile.establishmentConfirmed && workplace.scale !== 'unknown' && workplace.scaleConfirmedAt !== null,
      ...(workplace.segment ? { segment: workplace.segment } : {}),
    };
    const scopedShifts = periodShifts.filter((shift) => (shift.workplaceId ?? defaultId) === workplaceId);
    // Older callers may omit workplaceId. Attribute those legacy weeks to the
    // profile's/default workplace once instead of duplicating them per site.
    const scopedWeeks = input.weeklyHolidayWeeks?.filter((week) => week.workplaceId === workplaceId || (week.workplaceId === undefined && workplaceId === defaultId));
    return { workplace, result: privateMonth({ ...input, shifts: scopedShifts, ...(scopedWeeks ? { weeklyHolidayWeeks: scopedWeeks } : {}) }, scopedProfile) };
  });
  return aggregatePrivateResults(entries, input);
}

function publicMonth(input: CalcInput, profile: PublicServantProfile): MonthResult {
  const rate = input.tables.publicServantRates[profile.rateEntryId];
  if (!rate) {
    return {
      items: [], allowanceSubtotal: range(0), evidenceGrade: 'C',
      missingInputs: ['supportedPublicServantRateTable'], warnings: ['UNSUPPORTED_RATE_TABLE'], basis: [],
      provenance: { calculationSchemaVersion: input.calculationSchemaVersion, tableVersions: input.tables.versions, calculatedAt: input.calculatedAt ?? new Date().toISOString() },
    };
  }
  const entries: Array<[PayItem['kind'], number, number, string]> = [
    ['psOvertime', profile.recognizedOvertimeMinutes, rate.overtimeHourly, 'recognized minutes × official overtime hourly ÷ 60'],
    ['psNight', profile.recognizedNightMinutes, rate.nightHourly, 'recognized minutes × official night hourly ÷ 60'],
    ['psHoliday', profile.recognizedHolidayDays, rate.holidayDaily, 'recognized days × official holiday daily'],
  ];
  const items: PayItem[] = [];
  const basis: BasisNode[] = [];
  for (const [kind, quantity, unit, formula] of entries) {
    if (quantity <= 0) continue;
    const amount = kind === 'psHoliday' ? Math.round(quantity * unit) : Math.round((quantity * unit) / 60);
    const basisId = `basis-${kind}`;
    items.push({ kind, amount: range(amount), ...(kind === 'psHoliday' ? {} : { minutes: range(quantity) }), basisId });
    basis.push({
      id: basisId, formula,
      variables: [
        { name: 'quantity', value: String(quantity), source: 'user' },
        { name: 'officialRate', value: String(unit), source: 'table' },
      ],
      source: input.tables.publicServantSource,
      assumptions: ['기관이 인정한 시간·일수를 사용하며 앱이 명령·인정 여부를 판단하지 않는다.'],
    });
  }
  const grade: EvidenceGrade = profile.institutionRuleConfirmed ? 'A' : 'C';
  return {
    items, allowanceSubtotal: addRanges(items.map((item) => item.amount)), evidenceGrade: grade,
    missingInputs: profile.institutionRuleConfirmed ? [] : ['institutionRuleConfirmed'], warnings: [], basis,
    provenance: { calculationSchemaVersion: input.calculationSchemaVersion, tableVersions: input.tables.versions, calculatedAt: input.calculatedAt ?? new Date().toISOString() },
  };
}

export function calcMonth(input: CalcInput): MonthResult {
  validateTables(input.tables);
  if (input.profile.sector === 'public') return publicMonth(input, input.profile);
  const privateProfile = input.profile;
  const periodShifts = input.shifts.filter((shift) => shift.date >= input.period.start && shift.date <= input.period.end);
  const ids = new Set(periodShifts.map((shift) => shift.workplaceId ?? privateProfile.workplaceId).filter(Boolean));
  return input.workplaces && ids.size > 1 ? multiWorkplacePrivateMonth(input, privateProfile, input.workplaces) : privateMonth(input, privateProfile);
}

export function compareCustomAllowances(result: MonthResult, allowances: CalcInput['allowances']): AllowanceComparison[] {
  const comparisonBlocked = result.missingInputs.length > 0
    || result.warnings.some((warning) => warning.startsWith('LEGAL_REVIEW_REQUIRED_') || warning === 'UNSUPPORTED_WORK_TIME_SCHEME');
  return allowances.map((allowance) => {
    const contractualAmount = result.items.find((item) => item.customAllowanceId === allowance.id)?.amount.min ?? 0;
    if (comparisonBlocked || !allowance.coverageConfirmed || allowance.legalCoverage === 'unknown' || allowance.legalCoverage === 'supplemental') {
      return { allowanceId: allowance.id, legalCoverage: allowance.legalCoverage, status: 'not_comparable', contractualAmount };
    }
    const kinds: PayItem['kind'][] = allowance.legalCoverage === 'includesNight' ? ['nightPremium']
      : allowance.legalCoverage === 'includesOvertime' ? ['overtimePremium']
        : ['holidayPremium', 'holidayOverPremium'];
    const statutoryAmount = addRanges(result.items.filter((item) => kinds.includes(item.kind)).map((item) => item.amount));
    const difference = { min: statutoryAmount.min - contractualAmount, max: statutoryAmount.max - contractualAmount };
    const status = statutoryAmount.min > contractualAmount ? 'shortfall'
      : statutoryAmount.max <= contractualAmount ? 'covered'
        : 'range_overlap';
    return { allowanceId: allowance.id, legalCoverage: allowance.legalCoverage, status, contractualAmount, statutoryAmount, difference };
  });
}

export function reconcilePaycheck(result: MonthResult, paycheck: PaycheckInput): ReconciliationRow[] {
  const calculatedByKind = new Map<PayItem['kind'], MoneyRange>();
  for (const item of result.items.filter((entry) => entry.kind !== 'customAllowance')) {
    const previous = calculatedByKind.get(item.kind) ?? range(0);
    calculatedByKind.set(item.kind, addRanges([previous, item.amount]));
  }
  return [...calculatedByKind.entries()].map(([canonicalKind, calculated]) => {
    const matching = paycheck.items.filter((item) => item.canonicalKind === canonicalKind);
    if (!matching.length) return { canonicalKind, status: 'missing_actual', calculated };
    const actual = matching.reduce((total, item) => total + item.amount, 0);
    if (result.evidenceGrade === 'C' || matching.some((item) => !item.scopeConfirmed)) return { canonicalKind, status: 'not_comparable', calculated, actual };
    const difference = { min: actual - calculated.max, max: actual - calculated.min };
    const status = calculated.min !== calculated.max && actual >= calculated.min && actual <= calculated.max ? 'within_range'
      : difference.max < -1000 ? 'shortfall'
        : difference.min > 1000 ? 'overage'
          : 'within_rounding';
    return { canonicalKind, status, calculated, actual, difference };
  });
}

export function reconcileActualDeposit(result: MonthResult, paycheck: PaycheckInput): ActualDepositReconciliation {
  const payslipTotal = paycheck.items.reduce((sum, item) => sum + item.amount, 0);
  if (!paycheck.items.length || paycheck.actualDeposit === undefined) {
    return { payslipTotal, ...(paycheck.actualDeposit === undefined ? {} : { actualDeposit: paycheck.actualDeposit }), grade: 'B', status: 'needs_input', message: '실입금액을 입력해주세요' };
  }
  const difference = payslipTotal - paycheck.actualDeposit;
  return {
    payslipTotal, actualDeposit: paycheck.actualDeposit, difference, grade: result.evidenceGrade === 'C' ? 'C' : 'B',
    status: Math.abs(difference) <= 1_000 ? 'match' : 'difference',
    message: Math.abs(difference) <= 1_000 ? '입금액과 지급총액이 같아요' : '공제 때문일 수 있어요',
  };
}

export function summarizeReconciliation(rows: ReconciliationRow[]): ReconciliationSummary {
  const pendingCount = rows.filter((row) => row.status === 'missing_actual' || row.status === 'not_comparable').length;
  const shortfallCount = rows.filter((row) => row.status === 'shortfall').length;
  const overageCount = rows.filter((row) => row.status === 'overage').length;
  const comparableCount = rows.length - pendingCount;
  return {
    status: rows.length === 0 || pendingCount > 0 || comparableCount === 0
      ? 'needs_input'
      : shortfallCount > 0 || overageCount > 0 ? 'difference' : 'match',
    comparableCount,
    pendingCount,
    shortfallCount,
    overageCount,
  };
}

function validateTables(tables: RateTables): void {
  if (!Number.isInteger(tables.schemaVersion) || tables.schemaVersion < 1) throw new Error('INVALID_RATE_TABLE_SCHEMA');
  const requiredKinds = ['privateRulePolicy', 'publicServantRates', 'fourInsurance', 'businessIncome3_3', 'employmentOnly', 'minWage'] as const;
  for (const kind of requiredKinds) {
    const metadata = tables.provenance?.[kind];
    if (!metadata || metadata.version !== tables.versions[kind]
      || !/^https:\/\//.test(metadata.sourceUrl)
      || !/^\d{4}-\d{2}-\d{2}T/.test(metadata.checkedAt)
      || !/^\d{4}-\d{2}-\d{2}$/.test(metadata.effectiveFrom)
      || (metadata.effectiveTo !== null && (!/^\d{4}-\d{2}-\d{2}$/.test(metadata.effectiveTo) || metadata.effectiveTo < metadata.effectiveFrom))
      || !/^[a-f0-9]{64}$/.test(metadata.checksum)) throw new Error('INVALID_RATE_TABLE_PROVENANCE');
  }
  if (!/^https:\/\//.test(tables.privateRulePolicy.source.url)) throw new Error('INVALID_RATE_TABLE_SOURCE');
  if (!/^https:\/\//.test(tables.publicServantSource.url)) throw new Error('INVALID_RATE_TABLE_SOURCE');
  if (tables.privateRulePolicy.classificationReview.dailyOvertimeAndHoliday === 'reviewed'
    && !tables.privateRulePolicy.classificationReview.reviewedAt) throw new Error('INVALID_LEGAL_REVIEW_METADATA');
  if (!/^\d{4}-\d{2}-\d{2}/.test(tables.privateRulePolicy.source.checkedAt)) throw new Error('INVALID_RATE_TABLE_CHECKED_AT');
}
